// components/NotificationSettings.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Form, Badge } from 'react-bootstrap';

const NotificationSettings = ({ 
  onEnableNotifications, 
  notificationToken, 
  isNotificationSupported 
}) => {
  const [notificationStatus, setNotificationStatus] = useState('default');
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    // Check current notification permission status
    if ('Notification' in window) {
      setNotificationStatus(Notification.permission);
    }
  }, [notificationToken]);

  const getStatusBadge = () => {
    switch (notificationStatus) {
      case 'granted':
        return <Badge bg="success">✅ Enabled</Badge>;
      case 'denied':
        return <Badge bg="danger">❌ Blocked</Badge>;
      default:
        return <Badge bg="warning">⏳ Not Set</Badge>;
    }
  };

  const getStatusMessage = () => {
    switch (notificationStatus) {
      case 'granted':
        return notificationToken 
          ? 'Push notifications are enabled and working!'
          : 'Permission granted, but token not generated yet.';
      case 'denied':
        return 'Push notifications are blocked. Please enable them in your browser settings.';
      default:
        return 'Click the button below to enable push notifications for important updates.';
    }
  };

  const handleTestNotification = () => {
    if (notificationStatus === 'granted') {
      new Notification('Test Notification', {
        body: 'This is a test notification from your app!',
        icon: '/favicon.ico'
      });
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">🔔 Push Notifications</h5>
      </Card.Header>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span>Status:</span>
          {getStatusBadge()}
        </div>

        <Alert 
          variant={notificationStatus === 'granted' ? 'success' : 'info'}
          className="mb-3"
        >
          {getStatusMessage()}
        </Alert>

        {!isNotificationSupported && (
          <Alert variant="warning">
            Push notifications are not supported in this browser.
          </Alert>
        )}

        <div className="d-grid gap-2">
          {notificationStatus !== 'granted' && isNotificationSupported && (
            <Button 
              variant="primary" 
              onClick={onEnableNotifications}
              disabled={notificationStatus === 'denied'}
            >
              Enable Push Notifications
            </Button>
          )}

          {notificationStatus === 'granted' && (
            <Button 
              variant="secondary" 
              onClick={handleTestNotification}
              size="sm"
            >
              Send Test Notification
            </Button>
          )}

          {notificationStatus === 'denied' && (
            <Alert variant="info" className="mt-2">
              <small>
                To enable notifications:
                <ol className="mb-0 mt-2">
                  <li>Click the lock/info icon in your browser's address bar</li>
                  <li>Change notifications from "Block" to "Allow"</li>
                  <li>Refresh the page</li>
                </ol>
              </small>
            </Alert>
          )}
        </div>

        {/* Debug info for development */}
        {notificationToken && (
          <div className="mt-3">
            <Form.Check
              type="checkbox"
              id="show-token"
              label="Show FCM Token (Debug)"
              checked={showToken}
              onChange={(e) => setShowToken(e.target.checked)}
            />
            
            {showToken && (
              <Alert variant="secondary" className="mt-2">
                <small>
                  <strong>FCM Token:</strong><br />
                  <code style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                    {notificationToken}
                  </code>
                </small>
              </Alert>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default NotificationSettings;